import { type IStrategyConfig, type IStrategyInfo, type IStrategyMetadata } from '../../../models/strategy/strategy-info-types'
import {
  type IStrategyComponentRecord,
  type ParametersPOSTUpdate,
  type ParametersRecord, type ParameterValue
} from '../../../models/strategy/strategy-component-types'
import dot from 'dot-object'
import { type StrategyComponentFormInputs } from '../../../components/card/strategy/strategy-component-card'
import { replaceExchangeShortname } from '../../exchange-utils'
import { formatToUTCTimestampFromUTCNumber } from '../../time-utils'
import { type GridRowModelUpdate } from '@mui/x-data-grid/models/gridRows'

export const EXCLUDED_FROM_DISPLAY_COMPONENTS: string[] = [
  'Books', 'Quoter', 'Hedger', 'Risk', 'Tradability.', 'Accounts'
]

export interface StrategyDataGridRow extends GridRowModelUpdate {
  id: string
  [key: string]: any
}

const threeDPFormatter = new Intl.NumberFormat('en-US',
  {
    style: 'decimal',
    minimumSignificantDigits: 1,
    maximumSignificantDigits: 8,
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
    useGrouping: false
  })

export const threeDPRounder = (value: string | number): string => {
  if (typeof value === 'string') {
    if (value === '' || value === '-') {
      return '-'
    }

    return (!Number.isNaN(value) && Number.isFinite(Number(value)))
      ? threeDPFormatter.format(Math.round((Number(value) + Number.EPSILON) * 1000) / 1000)
      : '-'
  } else {
    return (!Number.isNaN(value) && Number.isFinite(value))
      ? threeDPFormatter.format(Math.round((value + Number.EPSILON) * 1000) / 1000)
      : '-'
  }
}

export function isMinusZero (value: number): boolean {
  return 1 / value === -Infinity
}

export function flattenStrategies (strategies: IStrategyConfig[]): Record<string, string | undefined> {
  const map: Record<string, string | undefined> = {}
  for (let i = 0; i < strategies.length; i++) {
    const strategy: IStrategyConfig = strategies[i]
    for (const key of Object.keys(strategy)) {
      map[key] = strategy[key]
    }
  }
  return map
}

export function processStrategyMetadata (data: IStrategyInfo): IStrategyMetadata {
  return {
    timestamp: data.timestamp,
    strategyId: data.strategyId,
    updateType: data.updateType
  }
}

export function processComponentData (data: IStrategyInfo): IStrategyComponentRecord {
  const map: IStrategyComponentRecord = {}
  for (const componentName in data.strategy.components) {
    map[componentName] = {
      name: componentName,
      data: data.strategy.components[componentName]
    }
  }
  return map
}

function parseAsJsonIfValidString (str: string): any {
  try {
    return JSON.parse(str)
  } catch (e) {
    return null
  }
}

export function prepareFormStrategyParameters (parameters: ParametersRecord): Record<string, string | number | boolean> {
  // Parameters comprising array of values are split into individual parameters
  // and indexed by their position in the array e.g. quantity="100,100" --> quantity[0]: 100, quantity[1]: 100
  const processedParameters: Record<string, string | number | boolean> = {}
  Object.entries(parameters)
    .forEach(([parameter, value]) => {
      if (typeof value === 'string') {
        const parsedValue = parseAsJsonIfValidString(value)
        if (Array.isArray(parsedValue)) {
          parsedValue.forEach((arrValue, index) => {
            const newParameter = `${parameter}(${index})`
            processedParameters[newParameter] = arrValue
          })
        } else {
          const lowercaseValue = value.toLowerCase()
          if (lowercaseValue === 'true' || lowercaseValue === 'false') {
            processedParameters[parameter] = (lowercaseValue === 'true') // convert to boolean type
          } else {
            processedParameters[parameter] = value
          }
        }
      } else {
        if (Array.isArray(value)) {
          value.forEach((arrValue, index) => {
            const newParameter = `${parameter}(${index})`
            processedParameters[newParameter] = arrValue
          })
        } else {
          processedParameters[parameter] = value
        }
      }
    })
  return processedParameters
}

export function preparePOSTStrategyParameters (dirtyFields: object, strategyId: string, user: string, ipAddress: string,
  updateSnapshot: StrategyComponentFormInputs): ParametersPOSTUpdate {
  dot.keepArray = true

  const processedUpdate: Record<string, Record<string, ParameterValue>> = {}

  for (const dirtyComponent of Object.keys(dirtyFields)) {
    // @ts-expect-error dirtyFields has the same types as the update data
    const dotFormDirtyData = dot.dot(dirtyFields[dirtyComponent])
    const dotFormComponentData = dot.dot(updateSnapshot[dirtyComponent])

    const arrayLikeParameters = new Set() // Array parameters are treated as dirty overall if at least one element is dirty
    const deltaToMerge: Record<string, any> = {}
    // Process any changed values first
    for (const parameter of Object.keys(dotFormDirtyData)) {
      const posOfCloseBracket = parameter.lastIndexOf(')')
      if (posOfCloseBracket === parameter.length - 1) {
        const posOfOpenBracket = parameter.lastIndexOf('(')
        const arrParameterName = parameter.substring(0, posOfOpenBracket)
        const indexedArrParameterName = arrParameterName + '[' + parameter.substring(posOfOpenBracket + 1, posOfCloseBracket) + ']'
        arrayLikeParameters.add(arrParameterName)
        deltaToMerge[indexedArrParameterName] = dotFormComponentData[parameter]
      } else {
        deltaToMerge[parameter] = dotFormComponentData[parameter]
      }
    }

    const componentToAdd: Record<string, any> = {}
    // Load any unchanged array data before merging delta
    if (arrayLikeParameters.size > 0) {
      for (const [parameter, value] of Object.entries(dotFormComponentData)) {
        const posOfCloseBracket = parameter.lastIndexOf(')')
        if (posOfCloseBracket === parameter.length - 1) {
          const posOfOpenBracket = parameter.lastIndexOf('(')
          const arrParameterName = parameter.substring(0, posOfOpenBracket)
          if (arrayLikeParameters.has(arrParameterName)) {
            const indexedArrParameterName = arrParameterName + '[' + parameter.substring(posOfOpenBracket + 1, posOfCloseBracket) + ']'
            componentToAdd[indexedArrParameterName] = value
          }
        }
      }
    }

    Object.assign(componentToAdd, deltaToMerge)
    processedUpdate[dirtyComponent] = dot.dot(dot.object(componentToAdd))
  }

  return {
    timestamp: formatToUTCTimestampFromUTCNumber(Date.now()),
    strategyId,
    action: 'update_parameters',
    username: user,
    address: ipAddress,
    components: processedUpdate
  }
}

export function prepareAggregatedStrategyProperties (componentName: string,
  states: Record<string, string[] | any>, parameters: ParametersRecord
): Record<string, string | number | boolean> {
  const processedProperties: Record<string, string | number | boolean> = {}
  const uniqueSymbols = new Set()

  const combined = new Map(Object.entries(states))
  Object.entries(parameters).forEach(([key, value]) => combined.set(key, value))

  combined
    .forEach((value, property) => {
      // Create a set of all existing unique symbols
      let potentialSymbol: string = ''
      let propertyName: string = property
      if (property.includes('.')) {
        const symbolStr = property.substring(0, property.indexOf('.'))
        potentialSymbol = replaceExchangeShortname(symbolStr)
        propertyName = property.substring(property.indexOf('.') + 1)
        if (!uniqueSymbols.has(potentialSymbol)) {
          uniqueSymbols.add(potentialSymbol)
        }
      }

      // Format all properties from: symbol.property[optional index] --> symbol.component.property[optional index]
      if (typeof value === 'string') {
        const parsedValue = parseAsJsonIfValidString(value)
        if (Array.isArray(parsedValue)) {
          parsedValue.forEach((arrValue, index) => {
            const newProperty = createNewProperty(potentialSymbol, componentName, propertyName, index)
            processedProperties[newProperty] = arrValue
          })
        } else {
          const newProperty = createNewProperty(potentialSymbol, componentName, propertyName, -1)
          processedProperties[newProperty] = value
        }
      } else {
        if (Array.isArray(value)) {
          value.forEach((arrValue, index) => {
            const newProperty = createNewProperty(potentialSymbol, componentName, propertyName, index)
            processedProperties[newProperty] = arrValue
          })
        } else {
          const newProperty = createNewProperty(potentialSymbol, componentName, propertyName, -1)
          processedProperties[newProperty] = value
        }
      }
    })

  return processedProperties
}

function createNewProperty (symbol: string, componentName: string, propertyName: string, index: number): string {
  if (symbol.length > 0) {
    return index >= 0
      ? `${symbol}.${componentName}.${propertyName}(${index})`
      : `${symbol}.${componentName}.${propertyName}`
  } else {
    return index >= 0
      ? `${componentName}.${propertyName}(${index})`
      : `${componentName}.${propertyName}`
  }
}

function isSymbol (value: string): boolean {
  return value.includes('_') && !value.includes('CLIMB')
}

export function prepareAggregatedStrategyPropertiesForDataGrid (properties: Record<string, string | number | boolean>): StrategyDataGridRow[] {
  const processedRows: StrategyDataGridRow[] = []

  Object.keys(dot.object(properties))
    .filter(potentialSymbol => isSymbol(potentialSymbol))
    .forEach(symbol => {
      const symbolData = properties[symbol]
      processedRows.push(
        {
          id: symbol,
          ...dot.dot(symbolData),
          isBestAggBidSymbol: false,
          isBestAggAskSymbol: false
        }
      )
    })

  return processedRows
}

export interface LiveOrdersDataGridRow extends GridRowModelUpdate {
  orderId: string
  [key: string]: any
}

export function prepareLiveOrdersForDataGrid (liveOrders: Array<Record<string, string>>): LiveOrdersDataGridRow[] {
  const processedRows: LiveOrdersDataGridRow[] = []

  liveOrders.forEach(order => {
    processedRows.push({
      orderId: order.orderId,
      ...order
    })
  })

  return processedRows
}
